import { all, call, fork, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { getAuthToken } from "../../../../helpers/tokens";
import actions from "./actions";
import { createRequest, updateRequest, deleteRequest } from "../../crud/sagas";
import Api from "../../../../api";
import { defineGetSaga } from "../../../sagasHelper";

export function* getFullList() {
  yield takeEvery(
    actions.GET_FULL_LIST_REQUEST,
    function* ({ providerId, userId }) {
      const authToken = yield getAuthToken();
      if (authToken && authToken.token) {
        const { response, error } = yield call(
          Api.CommunicationMessages.getLatestCommunicationMessages,
          authToken.token,
          providerId,
          userId
        );
        if (response) {
          yield put(actions.getFullListSuccess(response));
        } else {
          yield put(actions.getFullListError(error));
        }
      }
    }
  );
}

export function* getAlertsList() {
  yield takeEvery(
    actions.GET_ALERTS_LIST_REQUEST,
    function* ({ providerId, userId }) {
      const authToken = yield getAuthToken();
      if (authToken && authToken.token) {
        const { response, error } = yield call(
          Api.CommunicationMessages.getLatestCommunicationMessages,
          authToken.token,
          providerId,
          userId,
          true // onlyAlerts
        );
        if (response) {
          yield put(actions.getAlertsListSuccess(response));
        } else {
          yield put(actions.getAlertsListError(error));
        }
      }
    }
  );
}

// Overview has independent request identities for Notes and Alerts. Reducers
// discard late responses after a page/user change or when the module unmounts.
export function* getOverviewPage({ payload: request }) {
  try {
    const authToken = yield call(getAuthToken);
    if (!authToken || !authToken.token) throw new Error("Please sign in to load notes.");
    const { response, total, error } = yield call(
      Api.CommunicationMessages.getLatestPage,
      authToken.token, request.providerId, request.userId,
      request.pageSize, (request.page - 1) * request.pageSize, request.kind === "alerts"
    );
    if (error) throw error;
    yield put({ type: actions.GET_OVERVIEW_PAGE_SUCCESS, request, payload: { messages: response, total } });
  } catch (error) {
    yield put({ type: actions.GET_OVERVIEW_PAGE_ERROR, request, error });
  }
}

export function* downloadCsvRequest() {
  // NOTE WE MAY NEED TO HANDLE TypeError as success
  //         if (error instanceof TypeError) {
  yield defineGetSaga(
    actions.COMMUNICATION_MESSAGES_DOWNLOAD_CSV_REQUEST,
    Api.CommunicationMessages.downloadCsv,
    actions.downloadCsvSuccess,
    actions.downloadCsvError
  );
}

// Keep the shared mutation workers, but route every automatic refresh through this
// feature's paginated GET. No other CRUD feature changes its URL or response shape.
export function* getPage() {
  const state = yield select((state) => state.ServiceUser.CommunicationMessages);
  if (!state.listContext) return;
  const query = { context: state.listContext, page: state.page, filters: state.filters };
  const { providerId, userId } = state.listContext;
  try {
    const authToken = yield call(getAuthToken);
    if (!authToken || !authToken.token) throw new Error("Please sign in to load notes.");
    const { response, total, error } = yield call(
      Api.CommunicationMessages.getPage, authToken.token, providerId, userId,
      state.page, state.pageSize, state.filters
    );
    if (error) throw error;
    const lastPage = Math.max(1, Math.ceil(total / state.pageSize));
    if (state.page > lastPage) {
      yield put(actions.getList({ providerId, userId, page: lastPage }));
      return;
    }
    yield put(actions.getListSuccess({ messages: response, total }, query));
  } catch (error) {
    yield put(actions.getListError(error, query));
  }
}

export default function* devSaga() {
  const path = "providers/:providerId/serviceusers/:userId/communicationmessages?includePrivate=true";
  yield all([
    takeLatest([
      actions.OPEN_LIST, actions.CLOSE_LIST, actions.CLEAR,
      actions.GET_LIST_REQUEST, actions.SET_FILTERS,
    ], getPage),
    fork(createRequest, actions, Api.Crud, path),
    fork(updateRequest, actions, Api.Crud, path, "id"),
    fork(deleteRequest, actions, Api.Crud, path, "id"),
    takeEvery(actions.GET_OVERVIEW_PAGE_REQUEST, getOverviewPage),
    getFullList(),
    getAlertsList(),
    downloadCsvRequest(),
  ]);
}
